import { IOverBookingListResponseData } from "@/api_framework/api_modals/Bookings";
import { IOrgServiceTypeWithServices, IOrgServiceWithSubServices, IOrgServicesGroupedByType } from "@/api_framework/api_modals/Org";
import Input from "@/app/components/ui/Inputs/Input";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import {
  setOverbookingFlag,
  setSelectedSlotDetails,
  setSelectedSlotStaffDetails,
  setSelectedSlotsClinicDetails,
  setSlotBookingFilters,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import clsx from "clsx";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import CommentSectionModal from "../../CommentSection";

export interface IOverBookingTableRowFormFields {
  allowed_date: string;
  over_booking_id: string;
  staff_id: string;
  staff_name: string;
  clinic_name: string;
  clinic_id: string;
  /* service details */
  service_id: string;
  service_name: string;
  sub_service_id: string;
  sub_service_name: string;
  /* service details */
  hours: string;
  minutes: string;
  end_hours: string;
  end_minutes: string;
}

const minuteItems = [
  { id: "00", title: "00" },
  { id: "05", title: "05" },
  { id: "10", title: "10" },
  { id: "15", title: "15" },
  { id: "20", title: "20" },
  { id: "25", title: "25" },
  { id: "30", title: "30" },
  { id: "35", title: "35" },
  { id: "40", title: "40" },
  { id: "45", title: "45" },
  { id: "50", title: "50" },
  { id: "55", title: "55" },
  { id: "59", title: "59" },
];

const SubServices = (subServices: IOrgServiceWithSubServices["sub_services"] = []) =>
  Array.from(new Map((subServices ?? []).map((subService) => [subService.sub_service_id, subService])).values());

const OverBookingTableForm: React.FC<{
  services: IOrgServiceWithSubServices[] | IOrgServicesGroupedByType;
  overBookingDetails: IOverBookingListResponseData;
  hideHeader: boolean;
}> = ({ overBookingDetails, hideHeader, services }) => {
  const { getRouteKey } = useRouter();
  const { routeState: customerProfileRouteState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { addMinutesAndCalculateTime } = useUtils();
  const formHook = useForm<IOverBookingTableRowFormFields>({
    mode: "onChange",
    defaultValues: {},
  });
  const dispatch = useAppDispatch();

  const {
    serviceTypes,
    subServiceToServiceIdMap,
    subServicesByServiceTypeIdMap,
    isServiceTypeMode,
  }: {
    serviceTypes: IOrgServiceTypeWithServices[];
    subServiceToServiceIdMap: Record<string, string>;
    subServicesByServiceTypeIdMap: Record<string, IOrgServiceWithSubServices["sub_services"]>;
    isServiceTypeMode: boolean;
  } = useMemo(() => {
    if (Array.isArray(services)) {
      const serviceTypesFromFlatData: IOrgServiceTypeWithServices[] = services.map((service) => ({
        id: service.service_id,
        name: service.service_name,
        services: [service],
      }));

      const subServiceMapFromFlatData = services.reduce(
        (final, service) => {
          (service.sub_services ?? []).forEach((subService) => {
            final[subService.sub_service_id] = service.service_id;
          });
          return final;
        },
        {} as Record<string, string>,
      );

      const subServicesByTypeMap = services.reduce(
        (final, service) => {
          final[service.service_id] = SubServices(service?.sub_services ?? []);
          return final;
        },
        {} as Record<string, IOrgServiceWithSubServices["sub_services"]>,
      );

      return {
        serviceTypes: serviceTypesFromFlatData,
        subServiceToServiceIdMap: subServiceMapFromFlatData,
        subServicesByServiceTypeIdMap: subServicesByTypeMap,
        isServiceTypeMode: false,
      };
    }

    const groupedServiceTypes = Object.values(services?.service_types ?? {});
    const subServiceMapFromApi = Object.entries(services?.sub_service_to_service_map ?? {}).reduce(
      (final, [subServiceId, details]) => {
        final[subServiceId] = details?.service_id;
        return final;
      },
      {} as Record<string, string>,
    );
    const subServicesByTypeMap: Record<string, IOrgServiceWithSubServices["sub_services"]> = {};

    groupedServiceTypes.forEach((serviceType) => {
      const groupedSubServices = SubServices(
        (serviceType.services ?? []).flatMap((service) => service?.sub_services ?? []),
      );

      (serviceType.services ?? []).forEach((service) => {
        (service.sub_services ?? []).forEach((subService) => {
          if (!subServiceMapFromApi[subService.sub_service_id]) {
            subServiceMapFromApi[subService.sub_service_id] = service.service_id;
          }
        });
      });

      subServicesByTypeMap[serviceType.id] = groupedSubServices;
    });

    return {
      serviceTypes: groupedServiceTypes,
      subServiceToServiceIdMap: subServiceMapFromApi,
      subServicesByServiceTypeIdMap: subServicesByTypeMap,
      isServiceTypeMode: true,
    };
  }, [services]);

  /* Redux States Starts */
  const { petDetails, customerId, petId } = customerProfileRouteState?.state as ICustomerProfileState;
  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  /* Redux States Ends */

  /* Actions /Handlers Starts */

  const handleSubmit = async (): Promise<boolean> => {
    const isValid = await formHook.trigger();
    if (!isValid) return false;

    const formValues = formHook.getValues();
    const resolvedServiceId = subServiceToServiceIdMap[formValues?.sub_service_id] ?? formValues?.service_id;

    logClick({
      event_type: "OVERBOOKING_BOOK_NOW_CLICK",
      scr_id: "BOOKING_PAGE",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      clinic_id: overBookingDetails?.clinic?.id,
      clinic_name: overBookingDetails?.clinic?.name,
      staff_id: overBookingDetails?.given_to_staff?.id,
      timestamp: Date.now(),
    });

    dispatch(
      setSlotBookingFilters({
        filters: {
          booking_date: overBookingDetails?.allowed_date,
          clinic_ids: [overBookingDetails?.clinic?.id],
          location_details: {
            latitude: "",
            longitude: "",
          },
          pet_species_id: petDetails?.pet_species_type_id,
          service_id: resolvedServiceId,
          ...(isServiceTypeMode ? { service_type_id: formValues?.service_id } : {}),
          slot_status: "1",
          staff_ids: [overBookingDetails?.given_to_staff?.id],
          sub_service_id: formValues?.sub_service_id,
        },
      }),
    );
    dispatch(setOverbookingFlag(true));
    dispatch(
      setSelectedSlotDetails({
        data: {
          slot_id: "",
          end_time: `${formValues?.end_hours}:${formValues?.end_minutes}:00`,
          start_time: `${formValues?.hours}:${formValues?.minutes}:00`,
        },
      }),
    );
    dispatch(
      setSelectedSlotStaffDetails({
        data: {
          experience: "",
          expertise: "",
          staff_id: overBookingDetails?.given_to_staff?.id,
          staff_name: overBookingDetails?.given_to_staff?.name,
        },
      }),
    );
    dispatch(
      setSelectedSlotsClinicDetails({
        data: {
          clinic_id: overBookingDetails?.clinic?.id,
          clinic_name: overBookingDetails?.clinic?.name,
          vetbuddy_clinic_name: "",
        },
      }),
    );
    return true;
  };

  const handleSubServiceSelect = (subServiceItem?: ISelectMenuItemData) => {
    const subServiceDuration = parseInt(subServiceItem?.state?.duration) ?? 0;
    const { hours, minutes, sub_service_id } = formHook.getValues();

    if (hours && minutes && sub_service_id) {
      const { hours: endHours, minutes: endMinutes } = addMinutesAndCalculateTime(
        `${hours}:${minutes}`,
        subServiceDuration,
      );

      formHook.setValue("end_hours", `${endHours}`);
      formHook.setValue("end_minutes", `${endMinutes}`);
    }
  };

  /* Actions/Handlers Ends */

  /* Constants Starts */
  const serviceWatch = formHook.watch("service_id");
  const subServiceWatch = formHook.watch("sub_service_id");
  const subServices = subServicesByServiceTypeIdMap[serviceWatch] ?? [];
  const l1ServiceItems = useSelectMenuReducer(serviceTypes, "name", "id");
  const l2ServiceItems = useSelectMenuReducer(subServices, "sub_service_name", "sub_service_id", {
    stateKey: "duration",
  });
  const hoursItems: ISelectMenuItemData[] = overBookingDetails?.start_time_hours?.map((h) => ({
    id: h,
    title: h,
  }));

  /* Constants Ends */

  useEffect(() => {
    if (subServiceWatch && l2ServiceItems?.length > 0) {
      const subServiceItem = l2ServiceItems.find((ss) => ss.id === subServiceWatch);
      handleSubServiceSelect(subServiceItem);
    }
  }, [subServiceWatch, l2ServiceItems]);

  useEffect(() => {
    if (!subServiceWatch) return;
    const isSubServiceAvailable = l2ServiceItems.some((subServiceItem) => subServiceItem.id === subServiceWatch);
    if (!isSubServiceAvailable) {
      formHook.setValue("sub_service_id", "");
      formHook.setValue("sub_service_name", "");
    }
  }, [serviceWatch, subServiceWatch, l2ServiceItems, formHook]);

  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <table className="w-full">
        <thead className="border-b-2 pb-2">
          <tr>
            <th
              className={clsx(
                "px-3 py-3 text-sm text-left font-medium w-28",
                hideHeader ? " p-0 invisible" : "px-3 py-3",
              )}
            >
              Provider
            </th>
            <th
              className={clsx(
                "px-3 py-3 text-sm text-left font-medium w-24",
                hideHeader ? " p-0 invisible" : "px-3 py-3",
              )}
            >
              Clinic
            </th>
            <th
              className={clsx(
                "px-3 py-3 text-sm text-left font-medium w-24",
                hideHeader ? " p-0 invisible" : "px-3 py-3",
              )}
            >
              Date
            </th>
            <th
              className={clsx(
                "px-3 py-3 text-sm text-left font-medium w-36",
                hideHeader ? " p-0 invisible" : "px-3 py-3",
              )}
            >
              Service L1
            </th>
            <th
              className={clsx(
                "px-3 py-3 text-sm text-left font-medium w-36",
                hideHeader ? " p-0 invisible" : "px-3 py-3",
              )}
            >
              Service L2
            </th>
            <th
              className={clsx(
                "px-3 py-3 text-sm text-left font-medium w-48 pl-2",
                hideHeader ? " p-0 invisible" : "px-3 py-3",
              )}
            >
              Start Time
            </th>
            <th
              className={clsx(
                "px-3 py-3 text-sm text-left font-medium w-48 ",
                hideHeader ? " p-0 invisible" : "px-3 py-3",
              )}
            >
              End Time
            </th>
            <th
              className={clsx("px-3 py-3 text-sm text-center font-medium", hideHeader ? " p-0 invisible" : "px-3 py-3")}
            >
              Book
            </th>
          </tr>
        </thead>
        <tbody className="pt-2">
          <tr className="pt-2">
            <td className="text-gray-600 text-xs py-2 px-3">{overBookingDetails?.given_to_staff?.name}</td>
            <td className="text-gray-600  text-xs py-2 px-3">{overBookingDetails?.clinic?.name}</td>
            <td className="text-gray-600 text-xs py-2 px-3">{overBookingDetails?.allowed_date}</td>
            <td>
              <div>
                <input type="hidden" {...formHook.register("service_id")} />
                <SearchableSelectMenu
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("service_id", item.id);
                      formHook.setValue("service_name", item.title);
                      formHook.setValue("sub_service_id", "");
                      formHook.setValue("sub_service_name", "");
                      formHook.clearErrors("service_id");
                      formHook.clearErrors("sub_service_id");
                    }
                  }}
                  errorMessages={[{ message: "L1 Service Required", type: "required" }]}
                  fieldError={formHook.formState.errors.service_id}
                  register={formHook.register("service_id", {
                    required: true,
                  })}
                  className=""
                  showDropdownIcon
                  showTypedErrors={false}
                  placeholder="Select Service"
                  selectItems={l1ServiceItems}
                  defaultSelected={l1ServiceItems?.find((l1s) => l1s.id === serviceWatch)}
                  autoComplete="false"
                  containerClassName="w-full"
                />
              </div>
            </td>
            <td>
              <div>
                <input type="hidden" {...formHook.register("sub_service_id")} />
                <SearchableSelectMenu
                  disabled={l2ServiceItems?.length === 0}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("sub_service_id", item.id);
                      formHook.setValue("sub_service_name", item.title);
                      formHook.clearErrors("sub_service_id");
                    }
                  }}
                  listBoxClassName="w-72"
                  errorMessages={[{ message: "L2 Service Required", type: "required" }]}
                  fieldError={formHook.formState.errors.sub_service_id}
                  register={formHook.register("sub_service_id", {
                    required: true,
                  })}
                  placeholder="Select Sub Service"
                  showDropdownIcon
                  showTypedErrors={false}
                  containerClassName="ml-2 w-full"
                  selectItems={l2ServiceItems}
                  defaultSelected={l2ServiceItems?.find((l2s) => l2s.id === subServiceWatch)}
                  autoComplete="false"
                />
              </div>
            </td>
            <td>
              <div className="grid grid-cols-2 gap-1 pl-4">
                <div>
                  <NonSearchableSelectMenu
                    disabled={hoursItems?.length === 0}
                    onSelectItem={(item) => {
                      formHook.setValue("hours", item.id);
                    }}
                    showTypedErrors={false}
                    errorMessages={[{ message: "Required", type: "required" }]}
                    fieldError={formHook.formState.errors.hours}
                    register={formHook.register("hours", { required: true })}
                    selectItems={hoursItems}
                    defaultSelected={hoursItems?.filter((h) => h.id === formHook.watch("hours"))[0]}
                    showDropdownIcon
                    placeholder="Hours"
                  />
                </div>
                <div>
                  <NonSearchableSelectMenu
                    onSelectItem={(item) => {
                      formHook.setValue("minutes", item.id);
                    }}
                    showTypedErrors={false}
                    disabled={hoursItems?.length === 0}
                    errorMessages={[{ message: "Required", type: "required" }]}
                    fieldError={formHook.formState.errors.minutes}
                    register={formHook.register("minutes", { required: true })}
                    selectItems={minuteItems}
                    defaultSelected={minuteItems?.filter((h) => h.id === formHook.watch("minutes"))[0]}
                    showDropdownIcon
                    placeholder="Minutes"
                  />
                </div>
              </div>
            </td>
            <td>
              <div className="grid grid-cols-2 gap-1 pl-2">
                <div>
                  <Input
                    disabled
                    className="disabled:bg-gray-100 text-xs"
                    placeholder="Hour"
                    register={formHook.register("end_hours", { required: true })}
                    errorMessages={[{ message: "required", type: "required" }]}
                    fieldError={formHook.register("end_hours", { required: true })}
                  />
                </div>
                <div>
                  <Input
                    disabled
                    className="disabled:bg-gray-100 text-xs"
                    placeholder="Minutes"
                    register={formHook.register("end_minutes", { required: true })}
                    errorMessages={[{ message: "required", type: "required" }]}
                    fieldError={formHook.register("end_minutes", { required: true })}
                  />
                </div>
              </div>
            </td>
            <td className="pl-4 ">
              <div className="w-full h-full flex justify-center items-center flex-col">
                <CommentSectionModal
                  onClick={() => {
                    handleSubmit();
                  }}
                  buttonName="Book Now"
                  activeButton
                  overBooking
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  );
};

export default OverBookingTableForm;
